import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, addMonths, subMonths, differenceInDays } from 'date-fns';
import {
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Loader2, RefreshCw,
  Megaphone, Send, Calendar as CalendarIconAlt, Clock, Edit2, Trash2, Eye,
  Target, ExternalLink, LayoutGrid, List, GitBranch, ArrowRight
} from 'lucide-react';
import api from '../api';

const eventTypeColors = {
  campaign_launch: 'bg-blue-500',
  email_send: 'bg-green-500',
  social_post: 'bg-purple-500',
  ad_launch: 'bg-amber-500',
  event: 'bg-pink-500',
  deadline: 'bg-red-500',
  milestone: 'bg-cyan-500',
  other: 'bg-slate-500'
};

const eventTypeLabels = {
  campaign_launch: 'Campaign Launch',
  email_send: 'Email Send',
  social_post: 'Social Post',
  ad_launch: 'Ad Launch',
  event: 'Event',
  deadline: 'Deadline',
  milestone: 'Milestone',
  other: 'Other'
};

// Generate distinct colors for campaigns
const campaignColors = [
  { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-700', dot: 'bg-blue-500' },
  { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-700', dot: 'bg-green-500' },
  { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-700', dot: 'bg-purple-500' },
  { bg: 'bg-amber-100', border: 'border-amber-400', text: 'text-amber-700', dot: 'bg-amber-500' },
  { bg: 'bg-pink-100', border: 'border-pink-400', text: 'text-pink-700', dot: 'bg-pink-500' },
  { bg: 'bg-cyan-100', border: 'border-cyan-400', text: 'text-cyan-700', dot: 'bg-cyan-500' },
  { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-700', dot: 'bg-red-500' },
  { bg: 'bg-indigo-100', border: 'border-indigo-400', text: 'text-indigo-700', dot: 'bg-indigo-500' },
];

export const ContentCalendarPage = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarItems, setCalendarItems] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showEventDetailDialog, setShowEventDetailDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'timeline', 'list'
  
  // Filters
  const [filterProductLine, setFilterProductLine] = useState('all');
  const [filterEventType, setFilterEventType] = useState('all');
  const [filterCampaign, setFilterCampaign] = useState('all');
  const [productLines, setProductLines] = useState([]);
  
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'campaign_launch',
    start_date: '',
    end_date: '',
    related_campaign_id: ''
  });
  
  const [campaigns, setCampaigns] = useState([]);
  const [campaignColorMap, setCampaignColorMap] = useState({});

  useEffect(() => {
    fetchCalendarData();
    fetchCampaigns();
    fetchProductLines();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  useEffect(() => {
    // Assign colors to campaigns
    const colorMap = {};
    campaigns.forEach((campaign, idx) => {
      colorMap[campaign.id] = campaignColors[idx % campaignColors.length];
    });
    setCampaignColorMap(colorMap);
  }, [campaigns]);

  const fetchCalendarData = async () => {
    try {
      setRefreshing(true);
      const start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      
      let url = `/marketing/calendar?start_date=${start}&end_date=${end}`;
      if (filterProductLine !== 'all') {
        url = `/marketing/calendar/by-product-line?product_line_id=${filterProductLine}&start_date=${start}&end_date=${end}`;
      }
      
      const res = await api.get(url);
      setCalendarItems(res.data || []);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await api.get('/marketing/campaigns?limit=100');
      setCampaigns(res.data?.campaigns || res.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchProductLines = async () => {
    try {
      const res = await api.get('/product-lines');
      setProductLines(res.data || []);
    } catch (error) {
      console.error('Failed to fetch product lines:', error);
    }
  };

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setEditingEvent(null);
    setEventForm({
      title: '',
      description: '',
      event_type: 'campaign_launch',
      start_date: format(date, 'yyyy-MM-dd'),
      end_date: format(date, 'yyyy-MM-dd'),
      related_campaign_id: ''
    });
    setShowEventDialog(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    const startDate = event.scheduled_date || event.start_date;
    setSelectedDate(startDate ? parseISO(startDate) : new Date());
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      event_type: event.item_type || event.event_type || 'other',
      start_date: startDate || '',
      end_date: event.end_date || startDate || '',
      related_campaign_id: event.related_campaign_id || event.campaign_id || ''
    });
    setShowEventDialog(true);
  };

  const handleViewEventDetails = (event) => {
    setSelectedEvent(event);
    setShowEventDetailDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title || !eventForm.start_date) {
      toast.error('Title and start date are required');
      return;
    }
    
    try {
      setSaving(true);
      const payload = {
        ...eventForm,
        related_campaign_id: eventForm.related_campaign_id === 'none' ? '' : eventForm.related_campaign_id
      };
      
      if (editingEvent) {
        await api.put(`/marketing/calendar/${editingEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await api.post('/marketing/calendar', payload);
        toast.success('Event created');
      }
      
      setShowEventDialog(false);
      setEditingEvent(null);
      setEventForm({
        title: '',
        description: '',
        event_type: 'campaign_launch',
        start_date: '',
        end_date: '',
        related_campaign_id: ''
      });
      fetchCalendarData();
    } catch (error) {
      console.error('Failed to save event:', error);
      toast.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await api.delete(`/marketing/calendar/${eventId}`);
      toast.success('Event deleted');
      fetchCalendarData();
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    }
  };

  const getEventsForDate = (date) => {
    return calendarItems.filter(item => {
      const schedDate = item.scheduled_date || item.start_date;
      if (!schedDate) return false;
      const startDate = parseISO(schedDate);
      const endDate = item.end_date ? parseISO(item.end_date) : startDate;
      const dateMatch = date >= startDate && date <= endDate;
      
      if (filterEventType !== 'all') {
        const itemType = item.item_type || item.event_type;
        if (itemType !== filterEventType) return false;
      }
      
      if (filterCampaign !== 'all') {
        const campId = item.campaign_id || item.related_campaign_id;
        if (campId !== filterCampaign) return false;
      }
      
      return dateMatch;
    });
  };

  const getCampaignById = (id) => campaigns.find(c => c.id === id);

  const getEventCampaignColor = (event) => {
    const campId = event.campaign_id || event.related_campaign_id;
    if (campId && campaignColorMap[campId]) {
      return campaignColorMap[campId];
    }
    return null;
  };

  // Group events by campaign for timeline view
  const getEventsByCampaign = () => {
    const grouped = {};
    const noGroup = [];
    
    calendarItems.forEach(item => {
      const campId = item.campaign_id || item.related_campaign_id;
      if (campId) {
        if (!grouped[campId]) {
          grouped[campId] = [];
        }
        grouped[campId].push(item);
      } else {
        noGroup.push(item);
      }
    });
    
    return { grouped, noGroup };
  };

  const days = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate)
  });

  const firstDayOfMonth = startOfMonth(currentDate).getDay();
  const paddingDays = Array(firstDayOfMonth).fill(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { grouped: eventsByCampaign, noGroup: ungroupedEvents } = getEventsByCampaign();

  return (
    <div className="space-y-6" data-testid="content-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarIcon className="h-6 w-6" />
            Content Calendar
          </h1>
          <p className="text-muted-foreground">
            Plan and schedule your marketing campaigns and content
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 border rounded-lg p-1">
            <Button 
              variant={viewMode === 'calendar' ? 'secondary' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('calendar')}
            >
              <LayoutGrid className="h-4 w-4 mr-1" />
              Calendar
            </Button>
            <Button 
              variant={viewMode === 'timeline' ? 'secondary' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('timeline')}
            >
              <GitBranch className="h-4 w-4 mr-1" />
              Timeline
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4 mr-1" />
              List
            </Button>
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchCalendarData} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Button onClick={() => handleDateClick(new Date())} data-testid="add-event-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Event
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Campaign:</Label>
          <Select value={filterCampaign} onValueChange={setFilterCampaign}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Campaigns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Campaigns</SelectItem>
              {campaigns.map(campaign => (
                <SelectItem key={campaign.id} value={campaign.id}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${campaignColorMap[campaign.id]?.dot || 'bg-slate-400'}`} />
                    {campaign.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Product Line:</Label>
          <Select value={filterProductLine} onValueChange={setFilterProductLine}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Product Lines" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Product Lines</SelectItem>
              {productLines.map(pl => (
                <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Event Type:</Label>
          <Select value={filterEventType} onValueChange={setFilterEventType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Event Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Event Types</SelectItem>
              {Object.entries(eventTypeLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {(filterProductLine !== 'all' || filterEventType !== 'all' || filterCampaign !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setFilterProductLine('all'); setFilterEventType('all'); setFilterCampaign('all'); }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Campaign Legend */}
      {campaigns.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4" />
              Active Campaigns
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {campaigns.slice(0, 8).map(campaign => {
                const colors = campaignColorMap[campaign.id];
                return (
                  <Badge 
                    key={campaign.id}
                    variant="outline"
                    className={`cursor-pointer ${colors?.bg} ${colors?.border} ${colors?.text}`}
                    onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}
                  >
                    <div className={`w-2 h-2 rounded-full mr-2 ${colors?.dot}`} />
                    {campaign.name}
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Badge>
                );
              })}
              {campaigns.length > 8 && (
                <Badge variant="outline">+{campaigns.length - 8} more</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={handlePrevMonth}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <h2 className="text-xl font-semibold">
                {format(currentDate, 'MMMM yyyy')}
              </h2>
              <Button variant="ghost" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {paddingDays.map((_, index) => (
                <div key={`pad-${index}`} className="h-28 bg-slate-50 rounded-lg" />
              ))}
              
              {days.map((day) => {
                const events = getEventsForDate(day);
                const isToday = isSameDay(day, new Date());
                
                return (
                  <div
                    key={day.toISOString()}
                    className={`h-28 border rounded-lg p-1 cursor-pointer hover:bg-slate-50 transition-colors ${
                      isToday ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => handleDateClick(day)}
                    data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <div className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-0.5 mt-1 overflow-hidden">
                      {events.slice(0, 3).map((event) => {
                        const campColor = getEventCampaignColor(event);
                        const campaign = getCampaignById(event.campaign_id || event.related_campaign_id);
                        return (
                          <div
                            key={event.id}
                            className={`text-xs px-1.5 py-0.5 rounded cursor-pointer ${
                              campColor 
                                ? `${campColor.bg} ${campColor.text} border-l-2 ${campColor.border}`
                                : `text-white ${eventTypeColors[event.item_type] || eventTypeColors[event.event_type] || eventTypeColors.other}`
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewEventDetails(event);
                            }}
                            title={`${event.title}${campaign ? ` • ${campaign.name}` : ''}${event.status ? ` • ${event.status}` : ''}`}
                          >
                            <div className="truncate font-medium">{event.title}</div>
                            {campaign && (
                              <div className="truncate text-[10px] opacity-75">{campaign.name}</div>
                            )}
                          </div>
                        );
                      })}
                      {events.length > 3 && (
                        <div className="text-xs text-muted-foreground">
                          +{events.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline View - Grouped by Campaign */}
      {viewMode === 'timeline' && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={handlePrevMonth}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <h2 className="text-xl font-semibold">
                  {format(currentDate, 'MMMM yyyy')} - Campaign Timeline
                </h2>
                <Button variant="ghost" size="icon" onClick={handleNextMonth}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                {Object.entries(eventsByCampaign).length === 0 && ungroupedEvents.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <GitBranch className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No events scheduled this month</p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Grouped by Campaign */}
                    {Object.entries(eventsByCampaign).map(([campaignId, events]) => {
                      const campaign = getCampaignById(campaignId);
                      const colors = campaignColorMap[campaignId];
                      const sortedEvents = [...events].sort((a, b) => {
                        const dateA = a.scheduled_date || a.start_date;
                        const dateB = b.scheduled_date || b.start_date;
                        return new Date(dateA) - new Date(dateB);
                      });

                      return (
                        <div key={campaignId} className={`p-4 rounded-lg ${colors?.bg || 'bg-slate-50'} border ${colors?.border || 'border-slate-200'}`}>
                          {/* Campaign Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-4 h-4 rounded-full ${colors?.dot || 'bg-slate-400'}`} />
                              <div>
                                <h3 className={`font-semibold ${colors?.text || ''}`}>
                                  {campaign?.name || 'Unknown Campaign'}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {events.length} event{events.length !== 1 ? 's' : ''} • {campaign?.status || 'Unknown status'}
                                </p>
                              </div>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/marketing/campaigns/${campaignId}`)}
                            >
                              View Campaign
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </Button>
                          </div>

                          {/* Timeline */}
                          <div className="relative pl-6 border-l-2 border-dashed ml-2" style={{ borderColor: colors?.dot?.replace('bg-', '') || '#94a3b8' }}>
                            {sortedEvents.map((event, idx) => {
                              const eventDate = event.scheduled_date || event.start_date;
                              return (
                                <div key={event.id} className="relative mb-4 last:mb-0">
                                  {/* Timeline Dot */}
                                  <div 
                                    className={`absolute -left-[25px] w-3 h-3 rounded-full ${colors?.dot || 'bg-slate-400'} border-2 border-white`}
                                  />
                                  
                                  {/* Event Card */}
                                  <div 
                                    className="bg-white p-3 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                                    onClick={() => handleViewEventDetails(event)}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="font-medium">{event.title}</p>
                                        <p className="text-sm text-muted-foreground">
                                          {eventDate && format(parseISO(eventDate), 'MMM d, yyyy')}
                                          {event.end_date && event.end_date !== eventDate && (
                                            <span> → {format(parseISO(event.end_date), 'MMM d')}</span>
                                          )}
                                        </p>
                                      </div>
                                      <Badge variant="outline" className="text-xs">
                                        {eventTypeLabels[event.item_type] || eventTypeLabels[event.event_type] || 'Other'}
                                      </Badge>
                                    </div>
                                  </div>
                                  
                                  {/* Connection Line to Next */}
                                  {idx < sortedEvents.length - 1 && (
                                    <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 text-muted-foreground">
                                      <ArrowRight className="h-4 w-4 rotate-90" />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}

                    {/* Ungrouped Events */}
                    {ungroupedEvents.length > 0 && (
                      <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <CalendarIcon className="h-4 w-4" />
                          Standalone Events
                        </h3>
                        <div className="space-y-2">
                          {ungroupedEvents.map(event => {
                            const eventDate = event.scheduled_date || event.start_date;
                            return (
                              <div 
                                key={event.id}
                                className="bg-white p-3 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow flex items-center justify-between"
                                onClick={() => handleViewEventDetails(event)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-3 h-3 rounded-full ${eventTypeColors[event.item_type] || eventTypeColors[event.event_type] || eventTypeColors.other}`} />
                                  <div>
                                    <p className="font-medium">{event.title}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {eventDate && format(parseISO(eventDate), 'MMM d, yyyy')}
                                    </p>
                                  </div>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {eventTypeLabels[event.item_type] || eventTypeLabels[event.event_type] || 'Other'}
                                </Badge>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                All Events - {format(currentDate, 'MMMM yyyy')}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={handlePrevMonth}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={handleNextMonth}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {calendarItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No events scheduled</p>
              </div>
            ) : (
              <div className="space-y-2">
                {[...calendarItems]
                  .sort((a, b) => {
                    const dateA = a.scheduled_date || a.start_date;
                    const dateB = b.scheduled_date || b.start_date;
                    return new Date(dateA) - new Date(dateB);
                  })
                  .map((event) => {
                    const eventDate = event.scheduled_date || event.start_date;
                    const campaign = getCampaignById(event.campaign_id || event.related_campaign_id);
                    const campColor = getEventCampaignColor(event);
                    
                    return (
                      <div 
                        key={event.id} 
                        className={`p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors ${
                          campColor ? `border-l-4 ${campColor.border}` : ''
                        }`}
                        onClick={() => handleViewEventDetails(event)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${eventTypeColors[event.item_type] || eventTypeColors[event.event_type] || eventTypeColors.other}`} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium">{event.title}</p>
                              
                              {/* Event description preview */}
                              {event.description && (
                                <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{event.description}</p>
                              )}
                              
                              {/* Meta info row */}
                              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-2 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5" />
                                  {eventDate && format(parseISO(eventDate), 'MMM d, yyyy')}
                                  {event.end_date && event.end_date !== eventDate && (
                                    <span> - {format(parseISO(event.end_date), 'MMM d')}</span>
                                  )}
                                </span>
                                
                                {campaign && (
                                  <span 
                                    className={`flex items-center gap-1 cursor-pointer hover:underline ${campColor?.text || ''}`}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/marketing/campaigns/${campaign.id}`); }}
                                  >
                                    <Target className="h-3.5 w-3.5" />
                                    {campaign.name}
                                  </span>
                                )}
                                
                                {campaign?.budget && (
                                  <span className="text-xs">Budget: ${campaign.budget.toLocaleString()}</span>
                                )}
                              </div>
                              
                              {/* Campaign channels */}
                              {campaign?.channels && campaign.channels.length > 0 && (
                                <div className="flex items-center gap-1 mt-2">
                                  {campaign.channels.slice(0, 3).map((channel, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-[10px] capitalize">
                                      {channel.replace('_', ' ')}
                                    </Badge>
                                  ))}
                                  {campaign.channels.length > 3 && (
                                    <Badge variant="secondary" className="text-[10px]">+{campaign.channels.length - 3}</Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                            <Badge variant="outline" className="text-xs">{eventTypeLabels[event.item_type] || eventTypeLabels[event.event_type] || 'Other'}</Badge>
                            {event.status && (
                              <Badge variant={event.status === 'completed' ? 'default' : 'secondary'} className="text-xs capitalize">
                                {event.status.replace('_', ' ')}
                              </Badge>
                            )}
                            {hasRole(['admin', 'marketing_manager']) && (
                              <>
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleEditEvent(event); }}>
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium">Event Types</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-3">
            {Object.entries(eventTypeLabels).map(([type, label]) => (
              <div key={type} className="flex items-center gap-1.5">
                <div className={`w-3 h-3 rounded ${eventTypeColors[type]}`} />
                <span className="text-sm text-muted-foreground">{label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Event Detail Dialog - Enhanced with rich marketing details */}
      <Dialog open={showEventDetailDialog} onOpenChange={setShowEventDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${eventTypeColors[selectedEvent?.item_type] || eventTypeColors[selectedEvent?.event_type] || eventTypeColors.other}`} />
              {selectedEvent?.title}
            </DialogTitle>
            <DialogDescription>
              {selectedEvent && (selectedEvent.scheduled_date || selectedEvent.start_date) && 
                format(parseISO(selectedEvent.scheduled_date || selectedEvent.start_date), 'EEEE, MMMM d, yyyy')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              {/* Event Type & Status */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="capitalize">
                  {eventTypeLabels[selectedEvent.item_type] || eventTypeLabels[selectedEvent.event_type] || 'Other'}
                </Badge>
                {selectedEvent.status && (
                  <Badge variant={selectedEvent.status === 'completed' ? 'default' : selectedEvent.status === 'in_progress' ? 'secondary' : 'outline'}>
                    {selectedEvent.status.replace('_', ' ')}
                  </Badge>
                )}
                {selectedEvent.priority && (
                  <Badge variant={selectedEvent.priority === 'high' ? 'destructive' : selectedEvent.priority === 'medium' ? 'secondary' : 'outline'}>
                    {selectedEvent.priority} priority
                  </Badge>
                )}
              </div>

              {/* Description */}
              {selectedEvent.description && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm">{selectedEvent.description}</p>
                </div>
              )}

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 border rounded-lg">
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Start Date</p>
                  <p className="font-medium mt-1">
                    {(selectedEvent.scheduled_date || selectedEvent.start_date) && 
                      format(parseISO(selectedEvent.scheduled_date || selectedEvent.start_date), 'MMM d, yyyy')}
                  </p>
                </div>
                <div className="p-3 border rounded-lg">
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    {selectedEvent.end_date ? 'End Date' : 'Duration'}
                  </p>
                  <p className="font-medium mt-1">
                    {selectedEvent.end_date 
                      ? format(parseISO(selectedEvent.end_date), 'MMM d, yyyy')
                      : 'Single day event'}
                  </p>
                </div>
              </div>

              {/* Campaign Details - Enhanced */}
              {(selectedEvent.campaign_id || selectedEvent.related_campaign_id) && (() => {
                const campaign = getCampaignById(selectedEvent.campaign_id || selectedEvent.related_campaign_id);
                const campColor = getEventCampaignColor(selectedEvent);
                if (!campaign) return null;
                
                return (
                  <div className={`p-4 rounded-lg border-l-4 ${campColor?.border || 'border-slate-400'} bg-slate-50`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">Related Campaign</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}
                      >
                        View Details
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                    
                    <h4 className="font-semibold text-base mb-2">{campaign.name}</h4>
                    
                    {/* Campaign Meta Info */}
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      {campaign.status && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Status:</span>
                          <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                            {campaign.status}
                          </Badge>
                        </div>
                      )}
                      {campaign.campaign_type && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Type:</span>
                          <span className="capitalize">{campaign.campaign_type.replace('_', ' ')}</span>
                        </div>
                      )}
                      {campaign.budget && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Budget:</span>
                          <span className="font-medium">${campaign.budget.toLocaleString()}</span>
                        </div>
                      )}
                      {campaign.target_audience && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Audience:</span>
                          <span className="truncate">{campaign.target_audience}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Campaign Goals */}
                    {campaign.goals && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Goals:</span>
                        <p className="mt-1 text-slate-700 line-clamp-2">{campaign.goals}</p>
                      </div>
                    )}
                    
                    {/* Campaign Channels */}
                    {campaign.channels && campaign.channels.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <span className="text-sm text-muted-foreground">Channels:</span>
                        {campaign.channels.slice(0, 4).map((channel, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs capitalize">
                            {channel.replace('_', ' ')}
                          </Badge>
                        ))}
                        {campaign.channels.length > 4 && (
                          <Badge variant="outline" className="text-xs">+{campaign.channels.length - 4}</Badge>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Content/Media Preview (if available) */}
              {selectedEvent.content_preview && (
                <div className="p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Content Preview</p>
                  <p className="text-sm line-clamp-3">{selectedEvent.content_preview}</p>
                </div>
              )}

              {/* Media Attachments (if available) */}
              {selectedEvent.media_url && (
                <div className="p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Media</p>
                  <img 
                    src={selectedEvent.media_url} 
                    alt="Event media" 
                    className="rounded-lg max-h-32 object-cover"
                  />
                </div>
              )}

              {/* Tags/Labels */}
              {selectedEvent.tags && selectedEvent.tags.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {selectedEvent.tags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t">
                {hasRole(['admin', 'marketing_manager']) && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setShowEventDetailDialog(false); handleEditEvent(selectedEvent); }}>
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500" onClick={() => { setShowEventDetailDialog(false); handleDeleteEvent(selectedEvent.id); }}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="ml-auto"
                  onClick={() => setShowEventDetailDialog(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Event Edit/Create Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? 'Edit Event' : 'Add Calendar Event'}
            </DialogTitle>
            <DialogDescription>
              {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="Event title"
                data-testid="event-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select
                value={eventForm.event_type}
                onValueChange={(value) => setEventForm({ ...eventForm, event_type: value })}
              >
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(eventTypeLabels).map(([type, label]) => (
                    <SelectItem key={type} value={type}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                  data-testid="event-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Related Campaign</Label>
              <Select
                value={eventForm.related_campaign_id || 'none'}
                onValueChange={(value) => setEventForm({ ...eventForm, related_campaign_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a campaign (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${campaignColorMap[campaign.id]?.dot || 'bg-slate-400'}`} />
                        {campaign.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEvent} disabled={saving} data-testid="save-event-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingEvent ? 'Save Changes' : 'Add Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentCalendarPage;
